"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Pencil, UserX } from "lucide-react";
import { mockUsers, mockDepartments, UserRole } from "@/lib/mock-data";

export default function UsersPage() {
    const [searchTerm, setSearchTerm] = useState("");
    const [roleFilter, setRoleFilter] = useState<string>("all");

    const filteredUsers = mockUsers.filter((user) => {
        const matchesSearch =
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === "all" || user.role === roleFilter;
        return matchesSearch && matchesRole;
    });

    const getRoleBadge = (role: UserRole) => {
        const configs: Record<UserRole, { label: string; variant: "default" | "secondary" | "outline" }> = {
            admin: { label: "ผู้ดูแลระบบ", variant: "default" },
            executive: { label: "ผู้บริหาร", variant: "secondary" },
            staff: { label: "เจ้าหน้าที่", variant: "outline" },
            citizen: { label: "ประชาชน", variant: "outline" },
        };
        return configs[role];
    };

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="heading-sm text-primary">จัดการผู้ใช้งาน</h1>
                    <p className="text-muted-foreground">จัดการบัญชีผู้ใช้งานในระบบ</p>
                </div>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button>
                            <Plus className="mr-2 h-4 w-4" />
                            เพิ่มผู้ใช้
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>เพิ่มผู้ใช้ใหม่</DialogTitle>
                            <DialogDescription>กรอกข้อมูลผู้ใช้งานที่ต้องการเพิ่มในระบบ</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>ชื่อ-นามสกุล</Label>
                                <Input placeholder="กรอกชื่อ-นามสกุล" />
                            </div>
                            <div className="space-y-2">
                                <Label>อีเมล</Label>
                                <Input type="email" placeholder="example@customs.go.th" />
                            </div>
                            <div className="space-y-2">
                                <Label>เบอร์โทรศัพท์</Label>
                                <Input placeholder="02-XXX-XXXX" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>หน่วยงาน</Label>
                                    <Select>
                                        <SelectTrigger>
                                            <SelectValue placeholder="เลือกหน่วยงาน" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {mockDepartments.map((dept) => (
                                                <SelectItem key={dept.id} value={dept.id}>
                                                    {dept.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>บทบาท</Label>
                                    <Select>
                                        <SelectTrigger>
                                            <SelectValue placeholder="เลือกบทบาท" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="staff">เจ้าหน้าที่</SelectItem>
                                            <SelectItem value="admin">ผู้ดูแลระบบ</SelectItem>
                                            <SelectItem value="executive">ผู้บริหาร</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button>บันทึก</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="ค้นหาชื่อหรืออีเมล..."
                                className="pl-10"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <Select value={roleFilter} onValueChange={setRoleFilter}>
                            <SelectTrigger className="w-full sm:w-[180px]">
                                <SelectValue placeholder="บทบาท" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">ทุกบทบาท</SelectItem>
                                <SelectItem value="admin">ผู้ดูแลระบบ</SelectItem>
                                <SelectItem value="executive">ผู้บริหาร</SelectItem>
                                <SelectItem value="staff">เจ้าหน้าที่</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">รายชื่อผู้ใช้งาน</CardTitle>
                    <CardDescription>พบ {filteredUsers.length} รายการ</CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ชื่อ</TableHead>
                                <TableHead>อีเมล</TableHead>
                                <TableHead>หน่วยงาน</TableHead>
                                <TableHead>บทบาท</TableHead>
                                <TableHead>สถานะ</TableHead>
                                <TableHead className="w-[50px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.map((user) => {
                                const roleBadge = getRoleBadge(user.role);
                                const department = mockDepartments.find((d) => d.id === user.departmentId);
                                return (
                                    <TableRow key={user.id}>
                                        <TableCell className="font-medium">{user.name}</TableCell>
                                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                                        <TableCell>{department?.name || "-"}</TableCell>
                                        <TableCell>
                                            <Badge variant={roleBadge.variant}>{roleBadge.label}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            {user.isActive ? (
                                                <Badge variant="secondary" className="bg-green-100 text-green-700">
                                                    ใช้งาน
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary" className="bg-gray-100 text-gray-500">
                                                    ปิดใช้งาน
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon">
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem>
                                                        <Pencil className="mr-2 h-4 w-4" />
                                                        แก้ไข
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem className="text-destructive">
                                                        <UserX className="mr-2 h-4 w-4" />
                                                        {user.isActive ? "ปิดใช้งาน" : "เปิดใช้งาน"}
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
